import { jwtDecode } from "jwt-decode";
import API_BASE_URL from '../config/api';

// Get current user's email from JWT token
export function getCurrentUserEmail() {
    try {
        const token = localStorage.getItem("authToken");
        if (!token) return null;
        
        const decoded = jwtDecode(token);
        return decoded.sub || null; // JWT 'sub' claim contains the user email
    } catch (error) {
        console.error("Error getting current user email:", error);
        return null;
    }
}

// Check if the token is expired or about to expire (within 5 minutes)
export function isTokenExpired(token) {
    if (!token) return true;
    try {
        const { exp } = jwtDecode(token);
        // Return true if expired or expiring in next 5 minutes (300 seconds)
        return Date.now() >= (exp - 300) * 1000;
    } catch (error) {
        console.error("Error decoding token:", error);
        return true;
    }
}

// Refresh the access token using the refresh token
export async function refreshToken() {
    try {
        const refreshToken = localStorage.getItem("refreshToken");
        
        if (!refreshToken) {
            console.warn("No refresh token available");
            redirectToSignIn();
            return null;
        }

        // Send refresh token in Authorization header
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, { 
            method: "POST",
            headers: {
                "Authorization": `Bearer ${refreshToken}`,
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            console.error("Token refresh failed:", response.status);
            throw new Error("Token refresh failed");
        }

        const { access_token } = await response.json();
        localStorage.setItem("authToken", access_token);
        console.log("✅ Token refreshed successfully");
        return access_token;
    } catch (error) {
        console.error("Failed to refresh token:", error);
        redirectToSignIn();
        return null;
    }
}

// Load state from database on login
export async function loadStateFromDatabase() {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return null;
        
        const userEmail = getCurrentUserEmail();
        if (!userEmail) return null;
        
        // Fetch state from database
        const response = await fetch(`${API_BASE_URL}/api/user-state/load`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (response.ok) {
            const data = await response.json();
            
            if (data.state) {
                // Store in localStorage for faster access
                const homepageStateKey = `aletheon_homepage_state_${userEmail}`;
                const collaborationStateKey = `lastCollaborationConversation_${userEmail}`;
                
                if (data.state.homepage_state) {
                    localStorage.setItem(homepageStateKey, JSON.stringify(data.state.homepage_state));
                }
                
                if (data.state.collaboration_state) {
                    localStorage.setItem(collaborationStateKey, JSON.stringify(data.state.collaboration_state));
                }
                
                return data.state;
            } else {
                console.log('ℹ️ No saved state in database');
                return null;
            }
        } else {
            console.error('⚠️ Failed to load state from database');
            return null;
        }
    } catch (error) {
        console.error('❌ Error loading state from database:', error);
        return null;
    }
}

// Sync current state to database before logout
export async function syncStateToDatabase() {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return false;
        
        const userEmail = getCurrentUserEmail();
        if (!userEmail) return false;
        
        // Collect all user-specific state from localStorage
        const homepageStateKey = `aletheon_homepage_state_${userEmail}`;
        const collaborationStateKey = `lastCollaborationConversation_${userEmail}`;
        
        const homepageState = localStorage.getItem(homepageStateKey);
        const collaborationState = localStorage.getItem(collaborationStateKey);
        
        // Only sync if there's state to save
        if (!homepageState && !collaborationState) {
            return false;
        }
        
        // Send to database
        const response = await fetch(`${API_BASE_URL}/api/user-state/save`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                homepage_state: homepageState ? JSON.parse(homepageState) : null,
                collaboration_state: collaborationState ? JSON.parse(collaborationState) : null
            })
        });
        
        if (response.ok) {
            console.log('✅ State synced to database before logout');
            return true;
        } else {
            console.error('⚠️ Failed to sync state to database');
            return false;
        }
    } catch (error) {
        console.error('❌ Error syncing state to database:', error);
        return false;
    }
}

// Clear authentication tokens but preserve user-specific state for better UX
function clearApplicationData() {
    // Only clear authentication-related data
    // Keep user-specific state (prefixed with user email) so users can resume their work
    const keysToRemove = [
        'authToken',
        'refreshToken',
        'access_token',
        'userProfileData',
        'userData',
        // Note: NOT clearing user-specific state like 'aletheon_homepage_state_${userEmail}'
        // This allows users to resume their work when they log back in
    ];
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear all sessionStorage data
    sessionStorage.clear();
    
    console.log('🧹 Cleared authentication data (user state preserved for resume)');
}

// Redirect user to sign-in page with cleanup
export async function redirectToSignIn(reason = 'session_expired') {
    // Prevent redirect loop - don't redirect if already on sign-in/sign-up pages
    const currentPath = window.location.pathname;
    const publicPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
    
    if (publicPages.includes(currentPath)) {
        console.log('⏭️ Already on public page, skipping redirect');
        return;
    }
    
    console.log(`🔒 Logging out user - Reason: ${reason}`);
    
    // Sync state to database before clearing (with timeout to prevent hanging)
    try {
        await Promise.race([
            syncStateToDatabase(),
            new Promise(resolve => setTimeout(() => resolve(false), 2000)) // 2 second timeout
        ]);
    } catch (error) {
        console.error('Error syncing state before logout:', error);
    }
    
    // Clear ALL user data (authentication + application state)
    clearApplicationData();
    
    // Stop token refresh loop
    stopTokenRefreshLoop();
    
    // Redirect to sign-in with optional message
    const message = reason === 'token_expired' 
        ? '?message=Your session has expired. Please sign in again.'
        : reason === 'unauthorized'
        ? '?message=Please sign in to continue.'
        : '';
    
    window.location.href = `/signin${message}`;
}

// Logout function for manual logout
export function logout() {
    redirectToSignIn('manual_logout');
}

// Fetch with authentication handling and automatic logout on 401
export async function fetchWithAuth(url, options = {}) {
    // Don't process auth for public pages
    const currentPath = window.location.pathname;
    const publicPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
    
    if (publicPages.includes(currentPath)) {
        // Just make the request without auth handling
        const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
        return fetch(fullUrl, options);
    }
    
    let token = localStorage.getItem("authToken");

    // Check if token is expired before making request
    if (isTokenExpired(token)) {
        token = await refreshToken();
        if (!token) {
            // Refresh failed, redirectToSignIn was already called
            throw new Error("Authentication required");
        }
    }

    // Ensure URL is absolute
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    try {
        const response = await fetch(fullUrl, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${token}`
            }
        });

        // Handle 401 Unauthorized - Token expired or invalid
        if (response.status === 401) {
            console.warn('⚠️ Received 401 - Token invalid or expired');
            
            // Try to refresh token one more time
            const newToken = await refreshToken();
            
            if (!newToken) {
                // Refresh failed, user logged out
                redirectToSignIn('token_expired');
                throw new Error("Session expired");
            }

            // Retry the request with new token
            return fetch(fullUrl, {
                ...options,
                headers: {
                    ...options.headers,
                    Authorization: `Bearer ${newToken}`
                }
            });
        }

        return response;
    } catch (error) {
        // Network errors or other issues
        if (error.message === "Session expired") {
            throw error;
        }
        console.error('❌ Fetch error:', error);
        throw error;
    }
}

// Activity tracking for smart token refresh
let lastActivityTime = Date.now();
let refreshInterval = null;
let expiryMonitorInterval = null;

function updateActivity() {
    lastActivityTime = Date.now();
}

// Track user activity (mouse, keyboard, scroll)
function setupActivityTracking() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
    });
}

// Check if user has been active in the last 30 minutes
function isUserActive() {
    const thirtyMinutes = 30 * 60 * 1000;
    return (Date.now() - lastActivityTime) < thirtyMinutes;
}

// Continuous token expiry monitor - kicks out users immediately when token expires
export function startTokenExpiryMonitor() {
    // Clear any existing monitor
    if (expiryMonitorInterval) {
        clearInterval(expiryMonitorInterval);
    }
    
    // Check every 30 seconds for token expiry
    expiryMonitorInterval = setInterval(async () => {
        const token = localStorage.getItem("authToken");
        const refreshTokenValue = localStorage.getItem("refreshToken");
        
        // Skip check if on public pages
        const currentPath = window.location.pathname;
        const publicPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
        if (publicPages.includes(currentPath)) {
            return;
        }
        
        // If no token, user is already logged out
        if (!token) {
            return;
        }
        
        // Check if token is expired
        if (isTokenExpired(token)) {
            console.warn('⚠️ Token expired - attempting refresh...');
            
            // Try to refresh token
            if (refreshTokenValue) {
                const newToken = await refreshToken();
                if (!newToken) {
                    // Refresh failed - kick user out immediately
                    console.error('❌ Token refresh failed - logging out');
                    redirectToSignIn('token_expired');
                }
            } else {
                // No refresh token - kick user out immediately
                console.error('❌ No refresh token - logging out');
                redirectToSignIn('token_expired');
            }
        }
    }, 30 * 1000); // Check every 30 seconds
    
    console.log("✅ Token expiry monitor started (checks every 30s)");
}

// Stop the expiry monitor
export function stopTokenExpiryMonitor() {
    if (expiryMonitorInterval) {
        clearInterval(expiryMonitorInterval);
        expiryMonitorInterval = null;
        console.log("🛑 Token expiry monitor stopped");
    }
}

// Smart token refresh - only refresh if user is active
export function startTokenRefreshLoop() {
    // Set up activity tracking
    setupActivityTracking();
    
    // Clear any existing interval
    if (refreshInterval) {
        clearInterval(refreshInterval);
    }
    
    // Check every 5 minutes
    refreshInterval = setInterval(async () => {
        const token = localStorage.getItem("authToken");
        const hasRefreshToken = localStorage.getItem("refreshToken");
        
        // Only refresh if:
        // 1. Token is expiring (within 5 minutes)
        // 2. User has been active recently
        // 3. Refresh token exists
        if (hasRefreshToken && isUserActive() && isTokenExpired(token)) {
            console.log("🔄 Auto-refreshing token (user active)");
            await refreshToken();
        } else if (!isUserActive()) {
            console.log("💤 User inactive - skipping token refresh");
        }
    }, 5 * 60 * 1000); // Every 5 minutes
    
    console.log("✅ Token refresh loop started with activity tracking");
}

// Stop the refresh loop (useful for logout)
export function stopTokenRefreshLoop() {
    if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
        console.log("🛑 Token refresh loop stopped");
    }
    
    // Also stop expiry monitor
    stopTokenExpiryMonitor();
}
